import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmt, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, FileDown, Loader2, ClipboardList } from "lucide-react";
import { POStatusPill, PO_STATUS } from "@/components/po/POStatus";

export default function PurchaseOrdersPage() {
    const nav = useNavigate();
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");
    const [data, setData] = useState(null);
    useEffect(() => {
        let live = true;
        const t = setTimeout(() => api.get("/purchase-orders", { params: { status, q } })
            .then((r) => live && setData(r.data))
            .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load POs")), 250);
        return () => { live = false; clearTimeout(t); };
    }, [status, q]);
    const counts = data?.counts || {};
    const open = (counts.draft || 0) + (counts.sent || 0);

    return (
        <div data-testid="po-list-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Purchase Orders</h1>
                    <p className="text-slate-500 mt-1 text-sm">Orders to vendors · {open} open</p>
                </div>
                <Link to="/purchase-orders/new" className="hdb-btn hdb-btn-primary" data-testid="po-new-btn"><Plus size={14}/> New PO</Link>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
                {[["", "All"], ...Object.entries(PO_STATUS).map(([k, [l]]) => [k, l])].map(([k, l]) => (
                    <button key={k || "all"} onClick={() => setStatus(k)} data-testid={`po-filter-${k || "all"}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${status === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                        {l}{k && counts[k] ? <span className="ml-1 opacity-70">{counts[k]}</span> : null}
                    </button>
                ))}
                <div className="relative ml-auto">
                    <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400"/>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="PO no., vendor, ship-to…"
                           className="hdb-input !pl-8 !w-64" data-testid="po-search"/>
                </div>
            </div>
            <div className="hdb-card overflow-x-auto">
                {!data ? <div className="p-10 text-center"><Loader2 className="inline animate-spin text-slate-400"/></div>
                    : data.rows.length === 0 ? (
                        <div className="p-12 text-center text-slate-400" data-testid="po-empty">
                            <ClipboardList className="inline mb-2" size={22}/><br/>No purchase orders yet.
                        </div>
                    ) : (
                        <table className="hdb-table w-full text-sm">
                            <thead><tr><th className="text-left">PO No.</th><th className="text-left">Date</th><th className="text-left">Vendor</th>
                                <th className="text-left">Ship to</th><th className="text-left">Payment</th><th className="text-right">Items</th>
                                <th className="text-right">Total</th><th className="text-left">Status</th><th/></tr></thead>
                            <tbody>
                                {data.rows.map((p, i) => (
                                    <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/purchase-orders/${p.id}`)} data-testid={`po-row-${i}`}>
                                        <td className="hdb-mono font-bold">{p.po_number}</td>
                                        <td className="hdb-mono text-xs">{p.po_date_display}</td>
                                        <td className="font-semibold">{p.vendor?.name}</td>
                                        <td className="text-slate-600 text-xs">{p.ship_to?.name}</td>
                                        <td className="text-xs">{p.payment_terms}</td>
                                        <td className="hdb-num hdb-num-right">{p.line_items.length}</td>
                                        <td className="hdb-num hdb-num-right font-bold">{fmt(p.totals.total)}</td>
                                        <td><POStatusPill status={p.status} tid={`po-status-${i}`}/></td>
                                        <td className="text-right" onClick={(e) => e.stopPropagation()}>
                                            <a href={`${API}/purchase-orders/${p.id}/pdf?v=${encodeURIComponent(p.updated_at)}`} target="_blank" rel="noreferrer"
                                               className="hdb-btn hdb-btn-ghost text-xs" data-testid={`po-pdf-${i}`}><FileDown size={12}/> PDF</a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </div>
        </div>
    );
}
